import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import classes from './addRowModalCleanTime.module.scss';

interface FormData {
  roomId: number;
  coatingId: number | null; // Allow null for N/A
  cleanTypeId: number;
  outgoingJobTypeId: number;
  cleaningTimeMinutes: number;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface AddRowModalCleanTimeProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialValues?: FormData | null;
  isEdit: boolean;
  coatingOptions: DropdownOption[];
  roomOptions: DropdownOption[];
  cleanTypeOptions: DropdownOption[];
  outgoingJobTypeOptions: DropdownOption[];
}

const AddRowModalCleanTime = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit,
  coatingOptions,
  roomOptions,
  cleanTypeOptions,
  outgoingJobTypeOptions
}: AddRowModalCleanTimeProps) => {
  const [formData, setFormData] = useState<FormData>({
    roomId: 0,
    coatingId: null, // Default to null instead of 0
    cleanTypeId: 0,
    outgoingJobTypeId: 0,
    cleaningTimeMinutes: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const modalSessionRef = useRef<number>(0);
  const initializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (visible) {
      // Track modal session - increment when modal opens
      if (!initializedRef.current) {
        modalSessionRef.current += 1;
        initializedRef.current = true;

        // Only update form when modal first opens
        if (initialValues) {
          setFormData({ ...initialValues });
        } else {
          // Reset form for new entry
          setFormData({
            roomId: 0,
            coatingId: null, // Default to null instead of 0
            cleanTypeId: 0,
            outgoingJobTypeId: 0,
            cleaningTimeMinutes: 0
          });
        }
        setErrors({});
      }
    } else {
      // Modal closed, reset initialization flag
      initializedRef.current = false;
    }
    // Note: initialValues is intentionally not in dependencies to prevent re-initialization
    // when parent component re-renders after store updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.roomId || formData.roomId === 0) {
      newErrors.roomId = 'Room is required';
    }
    // Remove coating validation since it can be null (N/A)
    if (!formData.cleanTypeId || formData.cleanTypeId === 0) {
      newErrors.cleanTypeId = 'Clean Type is required';
    }
    if (!formData.outgoingJobTypeId || formData.outgoingJobTypeId === 0) {
      newErrors.outgoingJobTypeId = 'Outgoing Job Type is required';
    }

    // Clean Time validation
    if (formData.cleaningTimeMinutes === null || formData.cleaningTimeMinutes === undefined) {
      newErrors.cleaningTimeMinutes = 'Clean Time is required';
    } else if (formData.cleaningTimeMinutes < 0) {
      newErrors.cleaningTimeMinutes = 'Clean Time cannot be negative';
    } else if (formData.cleaningTimeMinutes > 720) {
      newErrors.cleaningTimeMinutes = 'Clean Time cannot exceed 720 minutes (12 hours)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
        // Close modal immediately on success
        setTimeout(() => {
          onHide();
        }, 100);
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for Clean Time data!');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    // For dropdown fields, ensure we get the value, not the option object
    if (field === 'coatingId' && value && typeof value === 'object' && 'value' in value) {
      value = value.value;
    }

    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Dialog
      header={isEdit ? 'Edit Clean Time Configuration' : 'Add New Clean Time Configuration'}
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '500px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formGroup}>
          <label>Room Type</label>
          <Dropdown
            value={formData.roomId}
            options={roomOptions}
            onChange={e => handleInputChange('roomId', e.value)}
            placeholder="Select Room"
            className={`${classes.dropdown} ${errors.roomId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.roomId && <small className="p-error">{errors.roomId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Coating</label>
          <Dropdown
            value={formData.coatingId}
            options={coatingOptions}
            onChange={e => handleInputChange('coatingId', e.value)}
            placeholder="Select Coating"
            className={`${classes.dropdown} ${errors.coatingId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
            showClear
            valueTemplate={option => {
              if (option === null || option === undefined) return 'N/A';
              if (typeof option === 'object' && option.label) return option.label;
              const selectedOption = coatingOptions.find(opt => opt.value === option);
              return selectedOption ? selectedOption.label : String(option);
            }}
          />
          {errors.coatingId && <small className="p-error">{errors.coatingId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Clean Type</label>
          <Dropdown
            value={formData.cleanTypeId}
            options={cleanTypeOptions}
            onChange={e => handleInputChange('cleanTypeId', e.value)}
            placeholder="Select Clean Type"
            className={`${classes.dropdown} ${errors.cleanTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.cleanTypeId && <small className="p-error">{errors.cleanTypeId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Outgoing Job Type</label>
          <Dropdown
            value={formData.outgoingJobTypeId}
            options={outgoingJobTypeOptions}
            onChange={e => handleInputChange('outgoingJobTypeId', e.value)}
            placeholder="Select Outgoing Job Type"
            className={`${classes.dropdown} ${errors.outgoingJobTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.outgoingJobTypeId && (
            <small className="p-error">{errors.outgoingJobTypeId}</small>
          )}
        </div>

        <div className={classes.formGroup}>
          <label>Clean Time (minutes)</label>
          <InputText
            value={formData.cleaningTimeMinutes.toString()}
            onChange={e =>
              handleInputChange('cleaningTimeMinutes', parseInt(e.target.value || '0', 10))
            }
            keyfilter="pint"
            placeholder="Enter clean time in minutes"
            className={`${classes.inputText} ${errors.cleaningTimeMinutes ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.cleaningTimeMinutes && (
            <small className="p-error">{errors.cleaningTimeMinutes}</small>
          )}
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />
        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalCleanTime;
